import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import GradeCardService from "../../_services/GradeCardService";

interface RatingScale {
  Id: number;
  Name: string;
  Description?: string;
  Options: Array<{ code: string; label: string }>;
}

interface ItemForm {
  name: string;
  itemType: string;
  options: string[] | null;
  ratingScaleId: number | null;
  sortOrder: number;
}

interface SectionForm {
  name: string;
  description: string;
  ratingScaleId: number | null;
  sortOrder: number;
  items: ItemForm[];
}

const ITEM_TYPES = [
  { value: "rating", label: "Rating Scale (E/G/W)" },
  { value: "checkbox-group", label: "Checkbox Group (letters, shapes)" },
  { value: "numeric", label: "Numeric (counts up to)" },
  { value: "text", label: "Text (remarks)" },
];

const GradeCardTemplateForm: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [termCount, setTermCount] = useState(2);
  const [termLabels, setTermLabels] = useState<string[]>(["Term I", "Term II"]);
  const [defaultRatingScaleId, setDefaultRatingScaleId] = useState<
    number | null
  >(null);
  const [sections, setSections] = useState<SectionForm[]>([]);
  const [ratingScales, setRatingScales] = useState<RatingScale[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load rating scales + existing structure if editing
  useEffect(() => {
    loadRatingScales();
    if (isEdit) loadStructure();
  }, []);

  const loadRatingScales = async () => {
    try {
      const res = await GradeCardService.listRatingScales();
      setRatingScales(res.data?.data || []);
    } catch (e) {
      console.error("Failed to load rating scales", e);
    }
  };

  const loadStructure = async () => {
    setLoading(true);
    try {
      const res = await GradeCardService.getStructure(Number(id));
      const data = res.data?.data;
      if (data) {
        setName(data.Name || "");
        setDescription(data.Description || "");
        setTermCount(data.TermCount || 2);
        const labels =
          typeof data.TermLabels === "string"
            ? JSON.parse(data.TermLabels)
            : data.TermLabels || ["Term I", "Term II"];
        setTermLabels(labels);
        setDefaultRatingScaleId(data.DefaultRatingScaleId || null);
        setSections(
          (data.sections || []).map((sec: any) => ({
            name: sec.Name,
            description: sec.Description || "",
            ratingScaleId: sec.RatingScaleId || null,
            sortOrder: sec.SortOrder,
            items: (sec.items || []).map((item: any) => ({
              name: item.Name,
              itemType: item.ItemType,
              options:
                typeof item.Options === "string"
                  ? JSON.parse(item.Options)
                  : item.Options,
              ratingScaleId: item.RatingScaleId || null,
              sortOrder: item.SortOrder,
            })),
          })),
        );
      }
    } catch (e: any) {
      setError(e.message || "Failed to load template");
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => {
    setSections([
      ...sections,
      {
        name: "",
        description: "",
        ratingScaleId: null,
        sortOrder: sections.length,
        items: [],
      },
    ]);
  };

  const removeSection = (idx: number) => {
    setSections(sections.filter((_, i) => i !== idx));
  };

  const updateSection = (idx: number, field: string, value: any) => {
    const updated = [...sections];
    (updated[idx] as any)[field] = value;
    setSections(updated);
  };

  const addItem = (sectionIdx: number) => {
    const updated = [...sections];
    updated[sectionIdx].items.push({
      name: "",
      itemType: "rating",
      options: null,
      ratingScaleId: null,
      sortOrder: updated[sectionIdx].items.length,
    });
    setSections(updated);
  };

  const removeItem = (sectionIdx: number, itemIdx: number) => {
    const updated = [...sections];
    updated[sectionIdx].items = updated[sectionIdx].items.filter(
      (_, i) => i !== itemIdx,
    );
    setSections(updated);
  };

  const updateItem = (
    sectionIdx: number,
    itemIdx: number,
    field: string,
    value: any,
  ) => {
    const updated = [...sections];
    (updated[sectionIdx].items[itemIdx] as any)[field] = value;
    setSections(updated);
  };

  const handleTermCountChange = (count: number) => {
    setTermCount(count);
    const newLabels = [...termLabels];
    while (newLabels.length < count) {
      newLabels.push(`Term ${newLabels.length + 1}`);
    }
    setTermLabels(newLabels.slice(0, count));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Template name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        name,
        description,
        termCount,
        termLabels,
        defaultRatingScaleId,
        sections: sections.map((sec, si) => ({
          name: sec.name,
          description: sec.description,
          ratingScaleId: sec.ratingScaleId,
          sortOrder: si,
          items: sec.items.map((item, ii) => ({
            name: item.name,
            itemType: item.itemType,
            options: item.options,
            ratingScaleId: item.ratingScaleId,
            sortOrder: ii,
          })),
        })),
      };

      if (isEdit) {
        await GradeCardService.updateStructure(Number(id), body);
      } else {
        await GradeCardService.createStructure(body);
      }
      history.push("/grade-card-templates");
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e.message || "Failed to save template",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 20 }} className="text-center">
        Loading...
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 900 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h4 style={{ margin: 0 }}>
          {isEdit ? "Edit" : "New"} Grade Card Template
        </h4>
        <div>
          <Button
            variant="secondary"
            size="sm"
            className="me-2"
            onClick={() => history.push("/grade-card-templates")}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Template"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Basic Info */}
      <div
        style={{
          background: "var(--surface-page)",
          padding: 16,
          borderRadius: 8,
          marginBottom: 20,
        }}
      >
        <Form.Group className="mb-3">
          <Form.Label>
            <strong>Template Name</strong>
          </Form.Label>
          <Form.Control
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Pre-Primary Progress Report"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Group>
        <div style={{ display: "flex", gap: 16 }}>
          <Form.Group style={{ flex: 1 }}>
            <Form.Label>Default Rating Scale</Form.Label>
            <Form.Select
              value={defaultRatingScaleId || ""}
              onChange={(e) =>
                setDefaultRatingScaleId(
                  e.target.value ? Number(e.target.value) : null,
                )
              }
            >
              <option value="">— Select —</option>
              {ratingScales.map((rs) => (
                <option key={rs.Id} value={rs.Id}>
                  {rs.Name}{rs.Description ? ` — ${rs.Description}` : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ width: 120 }}>
            <Form.Label>Terms</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={6}
              value={termCount}
              onChange={(e) => handleTermCountChange(Number(e.target.value))}
            />
          </Form.Group>
        </div>
        {termCount > 0 && (
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            {termLabels.map((label, i) => (
              <Form.Control
                key={i}
                size="sm"
                value={label}
                onChange={(e) => {
                  const updated = [...termLabels];
                  updated[i] = e.target.value;
                  setTermLabels(updated);
                }}
                style={{ width: 120 }}
                placeholder={`Term ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sections */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <h5 style={{ margin: 0 }}>Sections</h5>
        <Button variant="primary" size="sm" onClick={addSection}>
          + Add Section
        </Button>
      </div>

      {sections.length === 0 && (
        <div
          className="text-muted text-center"
          style={{
            padding: 30,
            border: "2px dashed var(--border-default)",
            borderRadius: 8,
          }}
        >
          No sections yet. Click "Add Section" to start building the grade
          card structure.
        </div>
      )}

      {sections.map((section, si) => (
        <div
          key={si}
          style={{
            border: "1px solid var(--border-default)",
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            background: "var(--surface-card)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <strong>Section {si + 1}</strong>
            <Button
              variant="danger"
              size="sm"
              onClick={() => removeSection(si)}
            >
              Remove
            </Button>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <Form.Group style={{ flex: 2 }}>
              <Form.Control
                value={section.name}
                onChange={(e) => updateSection(si, "name", e.target.value)}
                placeholder="Section name (e.g. Fine Motor Skills)"
              />
            </Form.Group>
            <Form.Group style={{ flex: 1 }}>
              <Form.Select
                value={section.ratingScaleId || ""}
                onChange={(e) =>
                  updateSection(
                    si,
                    "ratingScaleId",
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">Default Scale</option>
                {ratingScales.map((rs) => (
                  <option key={rs.Id} value={rs.Id}>
                    {rs.Name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          {/* Items */}
          <div style={{ marginLeft: 16 }}>
            {section.items.map((item, ii) => (
              <div
                key={ii}
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 8,
                  padding: 8,
                  background: "var(--surface-page)",
                  borderRadius: 4,
                }}
              >
                <Form.Control
                  size="sm"
                  style={{ flex: 2 }}
                  value={item.name}
                  onChange={(e) =>
                    updateItem(si, ii, "name", e.target.value)
                  }
                  placeholder="Item name"
                />
                <Form.Select
                  size="sm"
                  style={{ width: 200 }}
                  value={item.itemType}
                  onChange={(e) =>
                    updateItem(si, ii, "itemType", e.target.value)
                  }
                >
                  {ITEM_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>
                {item.itemType === "checkbox-group" && (
                  <Form.Control
                    size="sm"
                    style={{ flex: 2 }}
                    value={(item.options || []).join(", ")}
                    onChange={(e) =>
                      updateItem(
                        si,
                        ii,
                        "options",
                        e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      )
                    }
                    placeholder="Options (comma-separated, e.g. A, B, C)"
                  />
                )}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeItem(si, ii)}
                  style={{ minWidth: 32 }}
                >
                  ×
                </Button>
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => addItem(si)}
              style={{ marginTop: 4 }}
            >
              + Add Item
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GradeCardTemplateForm;
